import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { Box } from "@salestrak/ui";

import { InboxHeader } from "#app/components/inbox/inbox-header";
import { InboxList } from "#app/components/inbox/inbox-list";
import { useInbox } from "#app/hooks/inbox/use-inbox";
import { useInboxMutations } from "#app/hooks/inbox/use-inbox-mutations";
import { useInboxDateGroups } from "#app/hooks/inbox/use-inbox-date-groups";
import type { InboxFilter, InboxOrdering } from "#app/hooks/inbox/inbox-filters";
import {
  isInboxFilterAvailable,
  useInboxViewState,
} from "#app/hooks/inbox/inbox-filters";
import { useIssueView } from "#app/components/issue-view";
import { issueHref } from "#app/lib/utils/entity-hrefs";
import { copyText, openHref } from "#app/lib/utils/copy-actions";
import { AnalyticsContext, InboxEvent, trackEvent } from "#app/lib/analytics";
import type { InboxTrigger } from "#app/components/inbox/inbox-notification-item";
import type {
  IssueRow,
  NotificationRow as InboxStandardFragment,
} from "#app/lib/data";

const trackInbox = (event: (typeof InboxEvent)[keyof typeof InboxEvent], data: Record<string, unknown>) =>
  trackEvent({ context: AnalyticsContext.Inbox, event, data });

function InboxScreen() {
  const { workspace } = Route.useParams();
  const [activeTab, setActiveTab] = useState<"all" | "cleared">("all");
  // Filter and ordering outlive the session (per browser); the tab does not —
  // a reload lands on All, the live-triage view.
  const { ordering, setOrdering, filter, setFilter } = useInboxViewState();
  const showCleared = activeTab === "cleared";

  // Drop a filter that isn't offered on the tab being switched to (e.g. Unread
  // when moving to Cleared), so a stale where-clause can't linger.
  const setActiveTabSafe = useCallback((tab: "all" | "cleared", via: InboxTrigger) => {
    trackInbox(InboxEvent.SwitchTab, { tab, via });
    setActiveTab(tab);
    setFilter((current) =>
      current && !isInboxFilterAvailable(current, tab) ? null : current,
    );
  }, [setFilter]);

  const { items, unreadCount, loading, loadingMore, hasMore, fetchPage } =
    useInbox({ showCleared, filter });
  const { markRead, markUnread, markCleared, markUncleared } =
    useInboxMutations();

  const { groups, dateKeys } = useInboxDateGroups(items, ordering);
  const { openIssue } = useIssueView();

  const handleItemPress = useCallback(
    (item: InboxStandardFragment) => {
      trackInbox(InboxEvent.OpenNotification, { type: item.type, read: item.read });
      // Mark as read
      if (!item.read) {
        markRead([item]);
      }

      // Open the issue detail modal/sheet. A paragraph-mention notification
      // carries a customFieldId — deep-link to that field on the summary tab.
      // Otherwise fall back to the note/activity behavior.
      const issue = item.context.issue;
      if (issue) {
        const customFieldId = item.context.customFieldId ?? null;
        const target = customFieldId
          ? ({ initialTab: "summary", focusCustomFieldId: customFieldId } as const)
          : ({ initialTab: "activity", focusNoteId: item.context.noteId ?? null } as const);
        openIssue(
          {
            id: issue.id,
            issueId: issue.issueId,
            name: issue.name,
            stageId: issue.stageId,
            workflowId: issue.workflowId,
            sortOrder: 0,
            archived: false,
            noteCount: 0,
            hasUnreadNote: false,
            unreadNoteCount: 0,
            formCount: 0,
            issueType: issue.issueType
              ? {
                  id: issue.issueType.id,
                  issueTypeId: issue.issueType.issueTypeId,
                  name: issue.issueType.name,
                  icon: issue.issueType.icon ?? undefined,
                }
              : undefined,
            // Seed for immediate render; the issue view re-fetches full data by id.
          } as unknown as IssueRow,
          { ...target, source: "inbox" },
        );
      }
    },
    [markRead, openIssue],
  );

  const handleClear = useCallback(
    (item: InboxStandardFragment, via: InboxTrigger) => {
      trackInbox(InboxEvent.Clear, { via });
      markCleared([item]);
    },
    [markCleared],
  );

  const handleUnclear = useCallback(
    (item: InboxStandardFragment, via: InboxTrigger) => {
      trackInbox(InboxEvent.Unclear, { via });
      markUncleared([item]);
    },
    [markUncleared],
  );

  const handleToggleRead = useCallback(
    (item: InboxStandardFragment, via: InboxTrigger) => {
      trackInbox(InboxEvent.ToggleRead, { via, read: !item.read });
      if (item.read) {
        markUnread([item]);
      } else {
        markRead([item]);
      }
    },
    [markRead, markUnread],
  );

  const handleOpenInNewTab = useCallback(
    (item: InboxStandardFragment) => {
      const issue = item.context.issue;
      if (issue) openHref(issueHref(workspace, issue.workflowId, issue.issueId));
    },
    [workspace],
  );

  const handleCopyLink = useCallback(
    (item: InboxStandardFragment) => {
      const issue = item.context.issue;
      if (issue) {
        copyText(issueHref(workspace, issue.workflowId, issue.issueId), "Link");
      }
    },
    [workspace],
  );

  const handleClearAll = useCallback((via: InboxTrigger) => {
    if (items.length > 0) {
      trackInbox(InboxEvent.ClearAll, { via, count: items.length });
      markCleared(items);
    }
  }, [items, markCleared]);

  const handleFilterChange = useCallback(
    (next: InboxFilter | null, via: InboxTrigger) => {
      trackInbox(InboxEvent.ApplyFilter, { filter: next ?? "none", tab: activeTab, via });
      setFilter(next);
    },
    [activeTab, setFilter],
  );

  const handleOrderingChange = useCallback((next: InboxOrdering) => {
    trackInbox(InboxEvent.ChangeOrdering, { ordering: next });
    setOrdering(next);
  }, [setOrdering]);

  return (
    <Box direction="column" fill="flex">
      <InboxHeader
        activeTab={activeTab}
        onTabChange={setActiveTabSafe}
        ordering={ordering}
        onOrderingChange={handleOrderingChange}
        filter={filter}
        onFilterChange={handleFilterChange}
        unreadCount={unreadCount}
        onClearAll={handleClearAll}
        canClearAll={activeTab === "all" && items.length > 0}
      />
      <InboxList
        groups={groups}
        dateKeys={dateKeys}
        loading={loading}
        loadingMore={loadingMore}
        hasMore={hasMore}
        onLoadMore={() => fetchPage(items.length, 50)}
        onItemClick={handleItemPress}
        onClear={handleClear}
        onUnclear={handleUnclear}
        onToggleRead={handleToggleRead}
        onOpenInNewTab={handleOpenInNewTab}
        onCopyLink={handleCopyLink}
        activeTab={activeTab}
        filtered={filter !== null}
        onClearFilter={() => handleFilterChange(null, "click")}
      />
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/inbox")({
  component: InboxScreen,
});
